/**
 * Lingkup data: sumur mana, selang mana, batuan yang mana.
 *
 * Satu panel yang dipakai crossplot dan histogram, sebab keduanya menanyakan
 * hal yang sama sebelum menggambar apa pun — dan dua panel yang menanyakannya
 * dengan kata-kata yang sedikit berbeda adalah dua layar yang diam-diam
 * memplot dua himpunan data yang berbeda, lalu dibandingkan orang seolah
 * keduanya satu.
 *
 * Yang dipilih adalah *nama*, bukan barang: "Menggala Sand B" dan bukan zona
 * ke-37 milik KTI-09. Sebuah lapangan berisi enam sumur memuat zona bernama
 * sama enam kali, masing-masing dengan kedalamannya sendiri, dan pertanyaan
 * yang ditanyakan orang selalu tentang namanya.
 */

import { Fragment, useEffect, useMemo, useState } from "react";

import type { ScreenedWell, Screening, ScreenState, Well } from "../types";
import {
  Checkbox,
  Chip,
  Dropdown,
  Empty,
  Notice,
  NumberField,
  OptionalNumberField,
  Panel,
  Segmented,
  Slider,
} from "./ui";

export type IntervalMode = "all" | "zones" | "bounds";

/**
 * Sebuah batas selang, ditulis sebagai acuan dan bukan sebagai angka.
 *
 * `"marker:Top Menggala"`, `"zone_top:Sand A"`, `"zone_base:Sand A"` — dan
 * kosong berarti terbuka. Acuan dan bukan kedalaman sebab yang dimaksud orang
 * berlaku di tiap sumur dengan kedalamannya sendiri: puncak Sand A ada di
 * 2.050 m pada satu sumur dan 2.080 m pada sumur sebelahnya. Kedalaman tetap
 * bisa diminta, lewat `mdMin`/`mdMax`, dan di sana ia memang berarti angka
 * yang sama di mana-mana.
 */
export type BoundRef = string;

/**
 * Cara sumur dipilih, sebagaimana dikatakan orang: satu sumur, beberapa sumur,
 * atau sumur-sumur di sekitar sumur acuan. Disimpan terang-terangan dan bukan
 * ditebak dari `wells`, sebab "multi" yang kebetulan berisi satu sumur tetap
 * bukan "tunggal" — dan penjaringan radius yang dimatikan tidak boleh
 * meninggalkan jaringnya terpasang diam-diam.
 */
export type WellMode = "single" | "multi" | "radius";

/** Cara selang kedalaman dibatasi: seluruhnya, MD dari–sampai, antara dua marker, atau zona. */
export type DepthMode = "all" | "md" | "markers" | "zones";

export interface Scope {
  /**
   * Mode pemilihan sumur panel Sumber log. Tidak ada pada lingkup yang disimpan
   * sebelum panel itu ada — dan pada layar yang masih memakai `DataScope`
   * lama — lalu kuerinya dibangun seperti dulu, dari seluruh isian.
   */
  wellMode?: WellMode;
  /** Mode selang kedalaman panel Sumber log; lihat `wellMode`. */
  depthMode?: DepthMode;
  /** Id sumur; kosong berarti seluruh sumur lapangan. */
  wells: number[];
  interval: IntervalMode;
  zones: string[];
  /** Batas atas selang. Kosong berarti awal data. */
  topRef: BoundRef;
  /**
   * Batas bawah selang. Kosong di bawah sebuah *marker* berarti *marker
   * berikutnya di bawahnya*, dan itu bukan hal yang sama dengan seluruh sisa
   * sumur: begitulah orang menyebut selangnya sendiri, dan tiap sumur lalu
   * memakai marker antaranya sendiri alih-alih satu nama yang belum tentu ada
   * di mana-mana. Kosong di bawah tepi sebuah zona berarti ujung datanya —
   * zona sudah punya alasnya sendiri, dan yang memilih puncaknya tanpa alas
   * sedang meminta segala yang ada di bawah puncak itu.
   */
  baseRef: BoundRef;
  /** Batas MD, berlaku di atas zona atau marker. Null berarti tanpa batas. */
  mdMin: number | null;
  mdMax: number | null;
  /**
   * Sumur pusat jaring radius. Null berarti tidak ada jaring yang dipasang —
   * dan itu berbeda dengan radius yang sangat lebar: yang pertama tidak
   * menyentuh satu sumur pun, yang kedua menjaring seluruhnya lalu berkata
   * begitu di tabelnya.
   */
  centerWell: number | null;
  /** Jari-jari jaring, diketik dalam kilometer dan dikirim dalam meter. */
  radiusKm: number;
  /**
   * `surface` melingkari kepala sumur acuan; `trajectory` menurunkan pusat
   * lingkarannya mengikuti lintasannya, sehingga tetangga bisa terjaring pada
   * kedalaman tertentu saja.
   */
  radiusMode: "surface" | "trajectory";
  /** 1 berarti mati — "seluruh batuan" adalah Vsh ≤ 1. */
  vshMax: number;
  /** 0 berarti mati, dengan alasan yang sama. */
  swMin: number;
}

export const DEFAULT_SCOPE: Scope = {
  wells: [],
  interval: "all",
  zones: [],
  topRef: "",
  baseRef: "",
  mdMin: null,
  mdMax: null,
  centerWell: null,
  radiusKm: 5,
  radiusMode: "trajectory",
  vshMax: 1,
  swMin: 0,
};

/**
 * Lingkup sebagai parameter kueri.
 *
 * Yang mati tidak dikirim sama sekali dan bukan dikirim sebagai nilai ujungnya:
 * `vsh_max=1` menyuruh server menyaring dengan kurva VSH, dan sumur yang tidak
 * punya VSH lalu dilewati — padahal yang diminta orang adalah "jangan disaring".
 */
/** Mode sumur sebuah lingkup, ditebak dari isiannya bila belum tertulis. */
export function wellModeOf(scope: Scope): WellMode {
  if (scope.wellMode) return scope.wellMode;
  if (scope.centerWell !== null) return "radius";
  return scope.wells.length === 1 ? "single" : "multi";
}

/** Mode kedalaman sebuah lingkup, ditebak dari isiannya bila belum tertulis. */
export function depthModeOf(scope: Scope): DepthMode {
  if (scope.depthMode) return scope.depthMode;
  if (scope.interval === "zones") return "zones";
  if (scope.interval === "bounds") return "markers";
  return scope.mdMin !== null || scope.mdMax !== null ? "md" : "all";
}

/**
 * Lingkup yang hanya membawa isian mode yang sedang dipilih.
 *
 * Panel Sumber log menyimpan isian tiap mode supaya berpindah bolak-balik tidak
 * menghapus pilihan orang — tetapi yang dikirim hanya milik mode yang terpilih.
 * Batas MD yang tertinggal dari mode MD tidak boleh ikut memotong selang
 * marker, dan sumur acuan yang tertinggal dari mode radius tidak boleh ikut
 * menjaring sumur yang dicentang satu per satu.
 */
export function effectiveScope(scope: Scope): Scope {
  const out = { ...scope };
  if (scope.wellMode) {
    if (scope.wellMode === "radius") out.wells = [];
    else out.centerWell = null;
    if (scope.wellMode === "single") out.wells = scope.wells.slice(0, 1);
  }
  if (scope.depthMode) {
    out.interval = scope.depthMode === "zones" ? "zones" : scope.depthMode === "markers" ? "bounds" : "all";
    if (scope.depthMode !== "md") {
      out.mdMin = null;
      out.mdMax = null;
    }
  }
  return out;
}

export function scopeParams(raw: Scope): Record<string, string | undefined> {
  const scope = effectiveScope(raw);
  return {
    wells: scope.wells.length ? scope.wells.join(",") : undefined,
    zones: scope.interval === "zones" && scope.zones.length ? scope.zones.join(",") : undefined,
    top_ref: scope.interval === "bounds" ? scope.topRef || undefined : undefined,
    base_ref: scope.interval === "bounds" ? scope.baseRef || undefined : undefined,
    // Batas MD tidak terikat pada mode selangnya: "zona ini, tetapi hanya
    // seratus meter teratasnya" adalah satu pertanyaan dan bukan dua.
    md_min: scope.mdMin === null ? undefined : String(scope.mdMin),
    md_max: scope.mdMax === null ? undefined : String(scope.mdMax),
    // Radius dikirim dalam meter sebab itulah satuan koordinat sumurnya;
    // kilometer hidup di layar saja, tempat orang memang menyebut jaraknya.
    center_well: scope.centerWell === null ? undefined : String(scope.centerWell),
    radius: scope.centerWell === null ? undefined : String(Math.round(scope.radiusKm * 1000)),
    radius_mode: scope.centerWell === null ? undefined : scope.radiusMode,
    vsh_max: scope.vshMax < 1 ? scope.vshMax.toFixed(2) : undefined,
    sw_min: scope.swMin > 0 ? scope.swMin.toFixed(2) : undefined,
  };
}

/**
 * Lingkup yang dibaca kembali dari peramban, dipulihkan menjadi bentuk yang sah.
 *
 * Lingkup menetap di `localStorage`, jadi yang tersimpan di sana adalah bentuk
 * yang berlaku ketika ia ditulis — termasuk `interval: "markers"` berikut
 * sepasang `topMarker`/`baseMarker` dari sebelum batas selang boleh berupa tepi
 * zona. Dibiarkan apa adanya, mode itu tidak cocok dengan satu pun pilihan yang
 * ada sekarang: pemilihnya tampak kosong, dan kueri berangkat tanpa selang sama
 * sekali — layar lalu menggambar seluruh kedalaman tiap sumur tanpa sepatah
 * kata pun bahwa selang yang dulu dipilih sudah tidak berlaku.
 */
export function normalizeScope(raw: unknown): Scope {
  if (!raw || typeof raw !== "object") return DEFAULT_SCOPE;
  const stored = raw as Omit<Partial<Scope>, "interval"> & {
    interval?: string;
    topMarker?: unknown;
    baseMarker?: unknown;
  };
  const asMarker = (value: unknown) =>
    typeof value === "string" && value.trim() ? `marker:${value.trim()}` : "";
  return {
    ...DEFAULT_SCOPE,
    ...stored,
    interval:
      stored.interval === "zones"
        ? "zones"
        : stored.interval === "bounds" || stored.interval === "markers"
          ? "bounds"
          : "all",
    wells: Array.isArray(stored.wells) ? stored.wells : [],
    zones: Array.isArray(stored.zones) ? stored.zones : [],
    topRef: typeof stored.topRef === "string" ? stored.topRef : asMarker(stored.topMarker),
    baseRef: typeof stored.baseRef === "string" ? stored.baseRef : asMarker(stored.baseMarker),
    centerWell: typeof stored.centerWell === "number" ? stored.centerWell : null,
    radiusKm: typeof stored.radiusKm === "number" && stored.radiusKm > 0 ? stored.radiusKm : 5,
    radiusMode: stored.radiusMode === "surface" ? "surface" : "trajectory",
    wellMode: stored.wellMode === "single" || stored.wellMode === "multi" || stored.wellMode === "radius"
      ? stored.wellMode : undefined,
    depthMode: stored.depthMode === "all" || stored.depthMode === "md"
      || stored.depthMode === "markers" || stored.depthMode === "zones"
      ? stored.depthMode : undefined,
  };
}

/** Batas sebagaimana disebut orang: nama markernya, atau tepi zona yang mana. */
export function boundText(ref: BoundRef): string {
  const kind = ref.slice(0, ref.indexOf(":"));
  const name = ref.slice(ref.indexOf(":") + 1).trim();
  if (!ref.includes(":") || !name) return "";
  if (kind === "zone_top") return `Top of ${name}`;
  if (kind === "zone_base") return `Base of ${name}`;
  return name;
}

/**
 * Berapa sumur yang benar-benar memikul batas ini.
 *
 * Sebuah batas yang cuma ada di satu sumur dari enam adalah pilihan yang sah,
 * dan yang memilihnya berhak tahu bahwa lima sumur akan hilang dari plotnya
 * sebelum plotnya kosong.
 */
export function boundWells(wells: Well[], ref: BoundRef): number {
  const kind = ref.slice(0, ref.indexOf(":"));
  const name = ref.slice(ref.indexOf(":") + 1).trim().toLowerCase();
  if (!name) return wells.length;
  const has = (list: { name: string }[]) =>
    list.some((row) => row.name.trim().toLowerCase() === name);
  return wells.filter((well) => (kind === "marker" ? has(well.markers) : has(well.zones))).length;
}

/**
 * Pilihan batas: setiap marker lapangan, dan kedua tepi setiap zonanya.
 *
 * Keduanya berdiri di daftar yang sama, dan itu bukan kerapian melainkan
 * pertanyaannya sendiri: "dari Top Menggala sampai alas Sand B" adalah selang
 * yang biasa diminta orang, dan memisahkan marker dan zona ke dua mode yang
 * berbeda berarti menyuruhnya memilih salah satu lalu mencari sendiri
 * kedalaman yang lain di enam sumur.
 */
export function boundOptions(
  markers: MarkerName[],
  zones: { name: string }[],
): { value: string; label: string }[] {
  return [
    ...markers.map((row) => ({ value: `marker:${row.name}`, label: row.name })),
    ...zones.flatMap((zone) => [
      { value: `zone_top:${zone.name}`, label: `Puncak ${zone.name}` },
      { value: `zone_base:${zone.name}`, label: `Alas ${zone.name}` },
    ]),
  ];
}

/**
 * Sumur yang benar-benar memuat kurva.
 *
 * Marker sebuah sumur tanpa satu pun curve set tidak bisa menjadi batas selang
 * analisis: sumurnya sudah gugur sebelum selangnya sempat dipotong, sebab
 * crossplot maupun histogram melewatinya karena tidak punya kurva sumbunya.
 * Nama yang berdiri di daftar pilihan tetapi tidak bisa menyumbang satu titik
 * pun adalah pilihan yang menjanjikan sesuatu yang tidak dimilikinya — dan
 * pada lapangan yang sumur pemboran keringnya dimarkeri lengkap, nama semacam
 * itu justru yang paling banyak memenuhi daftarnya.
 */
export function loggedWells(wells: Well[]): Well[] {
  return wells.filter((well) => well.curve_sets.length > 0);
}

/**
 * Daftar pilihan berikut nilai yang sedang terpilih, sekalipun ia tidak lolos.
 *
 * Lingkup menetap di peramban, jadi sebuah pilihan yang dibuat ketika marker
 * sumur tanpa kurva masih boleh dipilih akan tetap ada di sana sesudahnya.
 * Dibiarkan hilang dari daftarnya, `Dropdown` memajangnya sebagai "—" — dan
 * yang berangkat di kueri tetap marker itu. Layar yang menyebut "—" sementara
 * kuerinya menyebut sebuah nama adalah layar yang memplot sesuatu yang tidak
 * bisa dibaca siapa pun dari apa yang tertulis padanya. Ia karena itu tetap
 * dipajang, berikut sebab ia tidak lagi ditawarkan, dan lenyap dari daftarnya
 * begitu penggantinya dipilih.
 */
export function withChosen(
  options: { value: string; label: string }[],
  value: string,
  label: string,
): { value: string; label: string }[] {
  if (!value || options.some((option) => option.value === value)) return options;
  return [{ value, label }, ...options];
}

/** `withChosen` bagi pemilih batas, yang nilainya sebuah `BoundRef`. */
export function withChosenBound(
  options: { value: string; label: string }[],
  ref: BoundRef,
): { value: string; label: string }[] {
  return withChosen(options, ref, `${boundText(ref)} — no curve`);
}

/** Nama zona lapangan ini sekali saja, terdangkal lebih dulu. */
export function zoneNames(wells: Well[]): { name: string; color: string; wells: number }[] {
  const seen = new Map<string, { name: string; color: string; depth: number; wells: number }>();
  for (const well of wells) {
    for (const zone of well.zones) {
      const row = seen.get(zone.name);
      if (row) {
        row.wells += 1;
        row.depth = Math.min(row.depth, zone.top_md);
      } else {
        seen.set(zone.name, { name: zone.name, color: zone.color, depth: zone.top_md, wells: 1 });
      }
    }
  }
  return [...seen.values()]
    .sort((a, b) => a.depth - b.depth)
    .map(({ name, color, wells: count }) => ({ name, color, wells: count }));
}

/**
 * Nama marker lapangan ini, terurut menurut kedalaman rata-ratanya.
 *
 * Urutan abjad akan menaruh "Base Sand" di atas "Top Menggala" — persis
 * terbalik dari susunan batuannya — dan daftar puncak yang urutannya terbalik
 * membuat pemilih puncak dan alas menjadi tebakan.
 */
export function markerRows(wells: Well[]): MarkerName[] {
  const depths = new Map<string, number[]>();
  for (const well of wells) {
    for (const marker of well.markers) {
      depths.set(marker.name, [...(depths.get(marker.name) ?? []), marker.md]);
    }
  }
  return [...depths.entries()]
    .map(([name, list]) => ({
      name,
      wells: list.length,
      mean: list.reduce((s, v) => s + v, 0) / list.length,
      min: Math.min(...list),
      max: Math.max(...list),
    }))
    .sort((a, b) => a.mean - b.mean);
}

export function markerNames(wells: Well[]): string[] {
  return markerRows(wells).map((row) => row.name);
}

export interface MarkerName {
  name: string;
  /** Berapa sumur yang benar-benar memikul marker bernama ini. */
  wells: number;
  mean: number;
  min: number;
  max: number;
}

/**
 * Selang MD sebuah pilihan marker pada sederet sumur.
 *
 * Dipakai untuk memberi batas pada penggeser kedalaman. Diambil dari marker
 * sumurnya sendiri dan bukan dari satu kedalaman rata-rata: sebuah penggeser
 * yang berhenti pada kedalaman rata-rata memotong sumur terdalam di
 * tengah-tengah selangnya sendiri.
 */
export function markerSpan(
  wells: Well[],
  topMarker: string,
  baseMarker: string,
): [number, number] | null {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const well of wells) {
    const sorted = [...well.markers].sort((a, b) => a.md - b.md);
    const top = topMarker ? sorted.find((m) => m.name === topMarker) : null;
    if (topMarker && !top) continue;
    const explicit = baseMarker ? sorted.find((m) => m.name === baseMarker) : null;
    if (baseMarker && !explicit) continue;
    const base =
      explicit ?? (top ? sorted.find((m) => m.md > top.md) ?? null : null);
    const from = top ? top.md : sorted[0]?.md ?? null;
    const to = base ? base.md : well.td_md ?? sorted[sorted.length - 1]?.md ?? null;
    if (from === null || to === null) continue;
    lo = Math.min(lo, Math.min(from, to));
    hi = Math.max(hi, Math.max(from, to));
  }
  return Number.isFinite(lo) && Number.isFinite(hi) && hi > lo ? [lo, hi] : null;
}

/**
 * Dropdown dengan namanya di *atas*, bukan di sebelahnya.
 *
 * `Dropdown` menaruh labelnya di kiri dan tombolnya selebar 100%, jadi sepasang
 * yang berdampingan di rel selebar tiga ratus piksel saling menindih: nama
 * marker yang panjang mendorong label sebelahnya ke bawah tombolnya sendiri.
 * Ditumpuk, keduanya muat berapa pun panjang namanya.
 */
export function StackedDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
      <Dropdown value={value} options={options} onChange={onChange} width="100%" compact />
    </div>
  );
}

/**
 * Kabar bahwa tidak ada yang tergambar, berikut sebabnya.
 *
 * Sebuah lingkup yang tak menyisakan satu pun sampel dulu menghasilkan kotak
 * gambar yang kosong dan diam: sumbunya tetap tergambar, garis matriksnya
 * tetap di tempatnya, dan tidak ada satu pun kalimat yang mengatakan bahwa
 * yang hilang adalah datanya — bukan layarnya. Yang paling sering terjadi
 * bukan kesalahan sama sekali: sumur aktif tidak punya kurva yang diminta, dan
 * lingkup bawaan sebuah layar adalah sumur aktif itu sendiri.
 *
 * Alasannya datang dari server sebagaimana adanya — sumur mana, kurang apa —
 * sebab hanya server yang tahu mana dari enam sumur yang gugur dan karena apa.
 */
export function ScopeEmpty({
  skipped,
  subject = "samples",
}: {
  skipped?: { well: string; reason: string }[];
  /** Yang tidak ada: "sampel" bagi histogram, "titik" bagi crossplot. */
  subject?: string;
}) {
  return (
    <Notice tone="warn">
      <span style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span>No {subject} at all in this scope, so there is nothing to draw.</span>
        {skipped && skipped.length > 0 && (
          <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {skipped.slice(0, 6).map((row) => (
              <span key={`${row.well}-${row.reason}`} style={{ font: "400 9.5px var(--font-mono)" }}>
                {row.well} — {row.reason}
              </span>
            ))}
            {skipped.length > 6 && (
              <span style={{ font: "400 9.5px var(--font-mono)" }}>
                …dan {skipped.length - 6} sumur lagi.
              </span>
            )}
          </span>
        )}
        <span>
          Loosen one of them: the wells in the Wells panel, its top and base in the
          Depth interval panel, or Vsh and Sw in the Rock filter panel.
        </span>
      </span>
    </Notice>
  );
}

export function DataScope({
  wells,
  scope,
  onChange,
  skipped,
  screening,
  selection = true,
}: {
  wells: Well[];
  scope: Scope;
  onChange: (next: Scope) => void;
  /**
   * `false` bila sumur, radius, dan selang sudah dipilih di panel Sumber log:
   * yang tersisa di sini penyaring batuan, jaring yang tertangkap, dan sumur
   * yang dilewati.
   */
  selection?: boolean;
  /** Sumur yang tidak masuk hitungan, sebagaimana dilaporkan server. */
  skipped?: { well: string; reason: string }[];
  /** Hasil penjaringan radius, sebagaimana dipulangkan bersama plotnya. */
  screening?: Screening | null;
}) {
  const [query, setQuery] = useState("");
  const zones = useMemo(() => zoneNames(wells), [wells]);
  const chosen = useMemo(() => new Set(scope.wells), [scope.wells]);

  // Hanya marker sumur yang berkurva. Yang lain tetap ada di menu Data dan
  // tetap tergambar di penampangnya; yang tidak boleh adalah berdiri di sini
  // sebagai batas yang tak sanggup menyumbang satu titik pun. Zona dibiarkan
  // apa adanya: yang diminta menyaring adalah markernya.
  const markers = useMemo(() => markerRows(loggedWells(wells)), [wells]);
  const bounds = useMemo(() => boundOptions(markers, zones), [markers, zones]);

  // Lapangan yang markernya ada tetapi seluruhnya di sumur tanpa kurva perlu
  // dibedakan dari lapangan yang memang belum dimarkeri sama sekali: yang
  // pertama sudah punya namanya dan kurang datanya, yang kedua sebaliknya.
  const hiddenMarkers = useMemo(
    () => markerRows(wells).length - markers.length,
    [wells, markers],
  );

  // Masuk ke mode batas tanpa satu pun batas terpilih berarti selang yang sama
  // dengan "seluruhnya", di bawah judul yang mengatakan sebaliknya. Karena itu
  // puncaknya dituliskan — bukan cuma ditampilkan sebagai pilihan pertama.
  useEffect(() => {
    if (scope.interval !== "bounds" || scope.topRef || scope.baseRef || !bounds.length) return;
    onChange({ ...scope, topRef: bounds[0].value });
  }, [scope, bounds, onChange]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? wells.filter((w) => w.name.toLowerCase().includes(needle)) : wells;
  }, [wells, query]);

  const set = (patch: Partial<Scope>) => onChange({ ...scope, ...patch });

  const toggleWell = (id: number) => {
    // Daftar kosong berarti "seluruhnya", jadi melepas satu sumur dari keadaan
    // itu harus lebih dulu menuliskan seluruhnya — kalau tidak, melepas satu
    // sumur justru akan memilih seluruhnya.
    const base = scope.wells.length ? scope.wells : wells.map((w) => w.id);
    const next = base.includes(id) ? base.filter((v) => v !== id) : [...base, id];
    set({ wells: next.length === wells.length ? [] : next });
  };

  const toggleZone = (name: string) => {
    const next = scope.zones.includes(name)
      ? scope.zones.filter((v) => v !== name)
      : [...scope.zones, name];
    set({ zones: next });
  };

  return (
    <>
      {selection && (
      <>
      <Panel title="Depth interval">
        <Segmented
          value={scope.interval}
          size="sm"
          options={[
            { value: "all", label: "Everything" },
            { value: "zones", label: "Zones" },
            { value: "bounds", label: "Bounds" },
          ]}
          onChange={(interval) => set({ interval: interval as IntervalMode })}
        />
        {scope.interval === "zones" &&
          (zones.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {zones.map((zone) => (
                <Chip
                  key={zone.name}
                  active={scope.zones.includes(zone.name)}
                  color={zone.color}
                  onClick={() => toggleZone(zone.name)}
                >
                  {zone.name}
                </Chip>
              ))}
            </div>
          ) : (
            <Empty>This field has no zone yet.</Empty>
          ))}
        {scope.interval === "bounds" &&
          (bounds.length ? (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <StackedDropdown
                  label="PUNCAK"
                  value={scope.topRef}
                  options={[
                    { value: "", label: "Start of data" },
                    ...withChosenBound(bounds, scope.topRef),
                  ]}
                  onChange={(topRef) => set({ topRef })}
                />
                <StackedDropdown
                  label="ALAS"
                  value={scope.baseRef}
                  options={[
                    {
                      value: "",
                      label: scope.topRef.startsWith("marker:")
                        ? "Next marker"
                        : "End of data",
                    },
                    ...withChosenBound(bounds, scope.baseRef),
                  ]}
                  onChange={(baseRef) => set({ baseRef })}
                />
              </div>
              {/* Cacah sumur berdiri sebagai keterangan dan bukan di dalam nama
                  pilihannya — nama yang dipanjangkan dengan angka mendorong
                  label sebelahnya keluar dari rel selebar tiga ratus piksel. */}
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                {[scope.topRef, scope.baseRef]
                  .filter(Boolean)
                  .map((ref) => `${boundText(ref)} in ${boundWells(wells, ref)}`)
                  .join("; ") || "Both ends open — the whole depth of every well"}
                {scope.topRef || scope.baseRef ? ` of ${wells.length} wells` : ""}
              </span>
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                {scope.baseRef
                  ? "Each well uses its own depth; a well missing one of the bounds is skipped and named."
                  : scope.topRef.startsWith("marker:")
                    ? "The base follows the next marker below the top — in each well, its own marker. Below the deepest marker the interval stays open to the end of the curve."
                    : "Below a zone edge, an empty base means the end of the data: a zone already carries its own base, and picking its top without a base asks for everything beneath it."}
              </span>
            </>
          ) : (
            <Notice tone="warn">
              {hiddenMarkers > 0
                ? `The markers exist — ${hiddenMarkers} names — but all of them sit on wells with no curve yet, so not one can bound an interval. Use MD bounds, or load the well logs first in the Data menu.`
                : "This field has neither a marker nor a zone. Use MD bounds, or set the markers first in the Data menu."}
            </Notice>
          ))}
        {scope.interval === "zones" && !scope.zones.length && (
          <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
            No zone picked yet — what is computed is the whole depth of every well.
          </span>
        )}

        {/* Batas MD, berlaku di atas apa pun yang dipilih di atasnya. "Zona ini,
            tetapi hanya seratus meter teratasnya" adalah satu pertanyaan, dan
            memaksanya memilih antara zona dan kedalaman berarti menyuruh orang
            mencari sendiri kedalaman zona itu di enam sumur. */}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <OptionalNumberField
              label="MD MIN"
              value={scope.mdMin}
              onChange={(mdMin) => set({ mdMin })}
              step={1}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <OptionalNumberField
              label="MD MAX"
              value={scope.mdMax}
              onChange={(mdMax) => set({ mdMax })}
              step={1}
            />
          </div>
        </div>
        {(scope.mdMin !== null || scope.mdMax !== null) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6, flex: 1 }}>
              MD bounds cut the choice above them; they do not replace it.
            </span>
            <Chip active={false} onClick={() => set({ mdMin: null, mdMax: null })}>
              Lepas
            </Chip>
          </div>
        )}
      </Panel>

      <Panel title={`Wells — ${scope.wells.length || wells.length} of ${wells.length}`}>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search wells…"
            aria-label="Search wells in this list"
            style={{
              flex: 1,
              minWidth: 0,
              height: 26,
              padding: "0 8px",
              background: "var(--panel-inset)",
              border: "1px solid var(--line-strong)",
              borderRadius: "var(--radius)",
              font: "400 11px var(--font-sans)",
              color: "var(--ink)",
              outline: "none",
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Chip active={scope.wells.length === 0} onClick={() => set({ wells: [] })}>
            All
          </Chip>
          <Chip
            active={scope.wells.length === 1}
            onClick={() => set({ wells: rows.length ? [rows[0].id] : [] })}
          >
            Just one
          </Chip>
        </div>
        <div className="scroll-y" style={{ maxHeight: 190, display: "flex", flexDirection: "column" }}>
          {rows.map((well) => (
            <Checkbox
              key={well.id}
              checked={scope.wells.length === 0 || chosen.has(well.id)}
              onChange={() => toggleWell(well.id)}
              label={well.name}
              dot={well.panel_color || well.symbol_color}
              meta={well.curve_sets.length ? undefined : "no curve"}
            />
          ))}
          {!rows.length && <Empty>No well matches.</Empty>}
        </div>
      </Panel>

      {/* Jaring radius. Berdiri tepat di bawah pemilih sumur sebab ia
          mempersempit pilihan yang sama — dan di atas penyaring batuan sebab
          urutannya memang begitu: sumur mana dulu, baru batuan yang mana. */}
      <Panel title="Radius filter">
        <Checkbox
          checked={scope.centerWell !== null}
          label="Filter wells by radius"
          onChange={(on) =>
            set({ centerWell: on ? (scope.centerWell ?? wells[0]?.id ?? null) : null })
          }
        />
        {scope.centerWell !== null && (
          <>
            <Dropdown
              label="CENTRE"
              value={scope.centerWell}
              options={wells.map((well) => ({ value: well.id, label: well.name }))}
              onChange={(centerWell) => set({ centerWell })}
            />
            <NumberField
              label="Radius"
              unit="km"
              value={scope.radiusKm}
              min={0.01}
              step={0.5}
              onChange={(radiusKm) => set({ radiusKm })}
            />
            <Segmented
              value={scope.radiusMode}
              size="sm"
              options={[
                { value: "surface", label: "Well head" },
                { value: "trajectory", label: "Along the track" },
              ]}
              onChange={(radiusMode) =>
                set({ radiusMode: radiusMode as Scope["radiusMode"] })
              }
            />
            <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
              {scope.radiusMode === "trajectory"
                ? "The centre of the circle descends along the reference well's track, so a neighbour can be caught at certain depths only. What is paired is TVDSS: on a deviated well the same MD is not the same depth."
                : "One circle at the head of the reference well. A well that passes contributes its whole depth."}
            </span>
            {screening?.note && <Notice tone="warn">{screening.note}</Notice>}
          </>
        )}
      </Panel>

      </>
      )}

      {screening && screening.wells.length > 0 && (
        <Panel
          title={`Caught — ${screening.kept} of ${screening.asked} wells`}
          meta={`${screening.radius_m >= 1000
            ? `${(screening.radius_m / 1000).toFixed(2).replace(/\.?0+$/, "")} km`
            : `${Math.round(screening.radius_m)} m`} from ${screening.center_name}`}
        >
          <ScreeningTable screening={screening} />
        </Panel>
      )}

      {/* Penyaring batuan. Chart densitas–neutron dibaca terhadap garis matriks,
          dan garis itu hanya berlaku di batuan bersih yang berair: serpih
          menaikkan neutron, hidrokarbon menurunkannya, dan awan yang memuat
          keduanya menjauhi garis tanpa ada yang salah pada matriksnya. */}
      <Panel title="Rock filter">
        <Slider
          label="Vsh max"
          value={scope.vshMax}
          min={0}
          max={1}
          step={0.01}
          onChange={(vshMax) => set({ vshMax })}
          format={(v) => (v >= 1 ? "all" : v.toFixed(2))}
        />
        <Slider
          label="Sw min"
          value={scope.swMin}
          min={0}
          max={1}
          step={0.01}
          onChange={(swMin) => set({ swMin })}
          format={(v) => (v <= 0 ? "all" : v.toFixed(2))}
        />
      </Panel>

      {skipped && skipped.length > 0 && (
        <Panel title={`Skipped — ${skipped.length} wells`}>
          {/* Nama dan alasannya, bukan cuma cacahnya: sebuah plot yang diam-diam
              kehilangan separuh lapangannya terlihat persis seperti plot yang
              lengkap. */}
          <div className="scroll-y" style={{ maxHeight: 120, display: "flex", flexDirection: "column", gap: 3 }}>
            {skipped.map((row) => (
              <span
                key={`${row.well}-${row.reason}`}
                style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}
              >
                {row.well} — {row.reason}
              </span>
            ))}
          </div>
        </Panel>
      )}
    </>
  );
}

/** Warna keadaan: yang lolos hidup, yang tidak lolos redup tetapi tetap terbaca.
 *
 *  Diekspor sebab peta jaring mewarnai sumur yang sama: peta yang mengeja
 *  "inside" dengan hijau sementara tabel di sebelahnya mengeja dengan biru
 *  adalah dua keterangan tentang satu sumur yang harus diterjemahkan orang di
 *  kepalanya sendiri. */
export const SCREEN_TONE: Record<ScreenState, string> = {
  "centre": "var(--accent)",
  "inside": "var(--ok-ink)",
  "partly inside": "var(--warn-ink)",
  "outside radius": "var(--ink-6)",
  "no coordinates": "var(--ink-6)",
};

/**
 * Hasil penjaringan sebagai tabel, satu baris tiap sumur yang diminta.
 *
 * Termasuk yang tidak lolos, dan itulah gunanya: daftar yang menyusut tanpa
 * menyebut siapa yang hilang tidak bisa dibedakan dari lapangan yang memang
 * cuma berisi sekian sumur. Kolom titiknya datang dari sampel yang benar-benar
 * terkumpul, bukan dari hitungan kedua — tabel yang menaksir sendiri berapa
 * yang "seharusnya" masuk cepat atau lambat akan berselisih dengan plot di
 * sebelahnya, dan yang dibaca orang adalah tabelnya.
 */
export function ScreeningTable({
  screening,
  maxRows,
  ranked = false,
}: {
  screening: Screening;
  /**
   * Baris terbanyak yang digambar. Sisanya tidak disembunyikan melainkan
   * dihitung dan disebut di baris terakhir: sebuah lapangan 1.402 sumur
   * menghasilkan 2.804 baris DOM di dalam rel setinggi 260 piksel, dan yang
   * dibaca orang dari tabel sepanjang itu tetap dua puluh baris teratasnya.
   */
  maxRows?: number;
  /**
   * Urutkan menurut jarak, nomori yang lolos, dan beri jaraknya kolom sendiri.
   *
   * Mati secara bawaan, dan itu bukan kehati-hatian belaka: urutan yang
   * dipulangkan server adalah urutan sumur yang **diminta** pemanggilnya, dan
   * pada layar yang daftarnya disusun orang sendiri — Correlation, Analysis —
   * urutan itulah yang dicari matanya. Yang menyalakannya adalah layar yang
   * memang menanyakan kedekatan, tempat "sumur keberapa yang terdekat"
   * pertanyaan yang sesungguhnya.
   */
  ranked?: boolean;
}) {
  // Kolom titik hanya berdiri kalau ada yang menghitungnya. Sekolom penuh
  // tanda hubung bukan keterangan melainkan derau, dan layar Analysis memang
  // tidak memplot apa pun untuk dihitung.
  const adaTitik = screening.wells.some((row) => row.points !== null);
  /** Lebar baris rincian, yang harus mengikuti kolom mana saja yang berdiri. */
  const kolom = (ranked ? 2 : 0) + 2 + (adaTitik ? 1 : 0);

  /**
   * Nomor urut tiap sumur yang lolos, terdekat lebih dulu.
   *
   * Yang lolos dinomori dan yang tidak lolos tidak, sebab yang ditanyakan
   * adalah urutan sumur yang **terjaring** — bukan urutan seluruh lapangan
   * menurut jarak. Yang lolos juga didahulukan alih-alih diselang-seling
   * menurut jarak semata: pada mode lintasan sebuah tetangga bisa terjaring
   * di kedalaman sementara kepalanya berdiri lebih jauh daripada sumur yang
   * tidak terjaring sama sekali, dan tabel yang menyisipkannya di tengah-tengah
   * membuat nomor urutnya melompat tanpa sebab yang terbaca.
   */
  const urutan = useMemo(() => {
    if (!ranked) return { rows: screening.wells, nomor: new Map<number, number>() };
    const lolos = (row: ScreenedWell) =>
      row.state === "centre" || row.state === "inside"
      || row.state === "partly inside";
    const jauh = (row: ScreenedWell) =>
      row.distance_m === null ? Number.POSITIVE_INFINITY : row.distance_m;
    const rows = [...screening.wells].sort((a, b) =>
      Number(lolos(b)) - Number(lolos(a)) || jauh(a) - jauh(b)
      || a.well.localeCompare(b.well));
    const nomor = new Map<number, number>();
    for (const row of rows) if (lolos(row)) nomor.set(row.well_id, nomor.size + 1);
    return { rows, nomor };
  }, [ranked, screening.wells]);

  const digambar = maxRows === undefined
    ? urutan.rows
    : urutan.rows.slice(0, maxRows);
  const sisa = urutan.rows.length - digambar.length;
  const cell = {
    padding: "5px 8px",
    font: "400 10.5px var(--font-mono)",
    color: "var(--ink-2)",
    textAlign: "right" as const,
    whiteSpace: "nowrap" as const,
  };
  const head = { ...cell, color: "var(--ink-5)" };
  /** Sel yang boleh membungkus — `tableLayout: fixed` mengurus lebarnya. */
  const wrapCell = { ...cell, whiteSpace: "normal" as const, wordBreak: "break-word" as const };

  const metres = (value: number | null) =>
    value === null ? "—" : `${Math.round(value).toLocaleString("en-US")}`;

  /** Jarak dibaca sebagai dekat atau jauh, dan itu tidak menuntut lima angka. */
  const km = (value: number | null) =>
    value === null ? "—" : `${(value / 1000).toFixed(1)} km`;

  return (
    <div
      className="scroll-y"
      style={{
        flex: "none",
        maxHeight: 260,
        border: "1px solid var(--line-strong)",
        borderRadius: "var(--radius-panel)",
        background: "var(--panel)",
      }}
    >
      {/* Dua baris tiap sumur, dan `tableLayout: fixed` supaya tabelnya tidak
          pernah bisa melebihi relnya.

          Lima kolom sejajar diukur 500 px di dalam wadah 252 px: yang tidak
          muat bukan satu kolom melainkan tiga, dan menggesernya ke urutan lain
          hanya memindahkan kolom mana yang hilang. Yang jarang dibaca — pita,
          tebal, jarak — karena itu turun ke baris keduanya sendiri, tempat
          selebar penuh, sementara baris pertama menyisakan tiga hal yang
          dicari orang sekali pandang: sumur mana, lolos atau tidak, dan berapa
          titik yang benar-benar sampai ke plot. */}
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--line)" }}>
            {ranked && <th style={{ ...head, textAlign: "right", width: "9%" }}>#</th>}
            <th style={{ ...head, textAlign: "left",
                         width: ranked ? "31%" : adaTitik ? "40%" : "50%" }}>Well</th>
            <th style={{ ...head, textAlign: "left",
                         width: ranked ? "26%" : adaTitik ? "37%" : "50%" }}>State</th>
            {ranked && <th style={{ ...head, width: "17%" }}>Distance</th>}
            {adaTitik && <th style={{ ...head, width: ranked ? "17%" : "23%" }}>Points</th>}
          </tr>
        </thead>
        <tbody>
          {digambar.map((row) => {
            // Yang tidak menyumbang satu titik pun diredupkan, tidak dibuang:
            // sebabnya justru itu yang dicari orang di sini.
            // Diredupkan hanya ketika ada cacah yang mengatakan ia tidak
            // menyumbang apa pun. Tanpa cacah, yang menentukan keadaannya.
            const tone = {
              opacity:
                row.points === null
                  ? row.state === "outside radius" || row.state === "no coordinates"
                    ? 0.55
                    : 1
                  : row.points > 0
                    ? 1
                    : 0.55,
            };
            const rincian = [
              row.bands.length
                ? row.bands.map(([top, base]) => `${top.toFixed(1)}–${base.toFixed(1)}`).join("; ")
                : row.state === "centre" || row.state === "inside"
                  ? "whole depth"
                  : null,
              row.thickness_m === null ? null : `${metres(row.thickness_m)} m`,
              // Jarak turun ke baris rincian hanya selama ia tidak punya kolom
              // sendiri; dua kali di satu baris adalah satu kali yang dibaca
              // dan satu kali yang ditanyakan apa bedanya.
              ranked || row.distance_m === null ? null : km(row.distance_m),
              row.reason ?? null,
            ].filter(Boolean);
            return (
              <Fragment key={row.well_id}>
                <tr style={{ ...tone, borderTop: "1px solid var(--line-soft)" }}>
                  {ranked && (
                    <td style={{ ...cell, color: "var(--ink-5)" }}>
                      {urutan.nomor.get(row.well_id) ?? "—"}
                    </td>
                  )}
                  <td style={{ ...wrapCell, textAlign: "left" }}>
                    {row.well}
                    {row.assumed_vertical && (
                      <span
                        title="No measured track yet — its position is assumed vertical below the well head."
                        style={{ marginLeft: 5, color: "var(--ink-6)" }}
                      >
                        ≈vertical
                      </span>
                    )}
                  </td>
                  <td style={{ ...wrapCell, textAlign: "left", color: SCREEN_TONE[row.state] }}>
                    {row.state}
                  </td>
                  {ranked && <td style={cell}>{km(row.distance_m)}</td>}
                  {adaTitik && (
                    <td style={cell}>{(row.points ?? 0).toLocaleString("en-US")}</td>
                  )}
                </tr>
                {rincian.length > 0 && (
                  <tr style={tone}>
                    <td
                      colSpan={kolom}
                      style={{
                        ...wrapCell,
                        textAlign: "left",
                        paddingTop: 0,
                        color: "var(--ink-6)",
                      }}
                      title={
                        row.overlap_m === null
                          ? undefined
                          : `Interval that can be screened — where both wells exist — ${metres(row.overlap_m)} m`
                      }
                    >
                      {rincian.join(" · ")}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {sisa > 0 && (
            <tr>
              <td
                colSpan={kolom}
                style={{ ...wrapCell, textAlign: "left", color: "var(--ink-6)" }}
              >
                … and {sisa.toLocaleString("en-US")} more wells, not drawn here
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
